import Phaser from 'phaser';
import { PALETTE } from '../config';
import { createPortrait, type AthleteView } from '../game/AthleteView';
import { AudioEngine } from '../game/AudioEngine';
import { countryFlag } from '../game/data/flags';
import { getDiscipline } from '../game/data/disciplines';
import { SaveManager } from '../game/SaveManager';
import {
  MAX_TRAIN_LEVEL, STAT_CAP, STAT_KEYS, STAT_STEP,
  fatigueKey, fatiguePenalty, nextTrainCost, trainLevel,
} from '../game/Economy';
import { Ads } from '../game/ads';
import { t } from '../game/i18n';
import type { Athlete, StatKey } from '../game/types';
import { addBackButton } from '../ui/BackButton';
import { Button } from '../ui/Button';
import { drawGradientBackground } from '../ui/Background';
import { drawPanel } from '../ui/Panel';
import { onResize, viewport } from '../ui/viewport';

/** Coins granted by watching a rewarded video (vs ~120 for a race win). */
const REWARD_COINS = 500;

/** Athlete training: spend coins to permanently raise stats (the repeatable coin sink). */
export class Training extends Phaser.Scene {
  private index = 0;
  private rig?: AthleteView;
  private panelObjects: Phaser.GameObjects.GameObject[] = [];
  private coinChip?: Phaser.GameObjects.Text;

  constructor() {
    super('Training');
  }

  init(data: { index?: number }): void {
    if (typeof data?.index === 'number') this.index = data.index;
  }

  private roster(): readonly Athlete[] {
    // Only owned athletes can be trained; the locked ones live in the race-select storefront.
    return SaveManager.instance.ownedRoster();
  }

  create(): void {
    this.panelObjects = [];
    const n = this.roster().length;
    this.index = ((this.index % n) + n) % n;
    this.build();
    const w0 = this.scale.width, h0 = this.scale.height;
    onResize(this, () => {
      if (Math.abs(this.scale.width - w0) > 2 || Math.abs(this.scale.height - h0) > 2) this.scene.restart({ index: this.index });
    });
  }

  private build(): void {
    const { w, h, cx, s } = viewport(this);
    drawGradientBackground(this);

    const groundH = Math.max(72, h * 0.12);
    const groundY = h - groundH;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.track, 1).fillRect(0, groundY, w, groundH);
    g.fillStyle(PALETTE.trackLine, 1).fillRect(0, groundY, w, 4);
    g.fillStyle(PALETTE.laneAccent, 0.16).fillEllipse(w * 0.26, groundY + 6, w * 0.32, 36);

    this.add.text(cx, h * 0.06, `🏋️ ${t('train.title')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(30 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);

    // Coins chip (top-right), refreshed after each purchase.
    this.coinChip = this.chip(w - 16 * s, 12 * s, s);

    addBackButton(this, s, () => this.scene.start('MainMenu'));

    // Rewarded video: free coins. Strong sink-balancer + the main monetisation lever.
    this.rewardButton(w * 0.24, h * 0.135, s);

    // Athlete portrait on the left third, with prev/next arrows + dots.
    const athlete = this.roster()[this.index]!;
    const portraitH = Phaser.Math.Clamp(h * 0.5, 220, 420);
    this.rig = createPortrait(this, w * 0.24, groundY + 2, athlete, portraitH);
    this.rig.setMotion(0);
    this.rig.container.setDepth(5);

    if (this.roster().length > 1) {
      this.arrow(Math.max(38, w * 0.05), h * 0.46, -1, s);
      this.arrow(w * 0.43, h * 0.46, 1, s);
    }
    this.dots(w * 0.24, groundY - 10 * s, s);

    this.renderPanel();
  }

  /** Rewarded-video button: watch an ad for free coins (gated by a cooldown). */
  private rewardButton(x: number, y: number, s: number): void {
    const save = SaveManager.instance;
    const ready = save.canClaimReward();
    const label = ready ? t('reward.cta', { n: REWARD_COINS }) : t('reward.wait', { s: Math.ceil(save.rewardReadyInMs() / 1000) });
    new Button(this, x, y, label, () => this.claimReward(), {
      width: Math.min(220, this.scale.width * 0.34), height: Math.max(42, 46 * s),
      color: ready ? PALETTE.good : PALETTE.trackLine, fontSize: Math.max(13, Math.round(15 * s)),
    });
  }

  private claimReward(): void {
    const save = SaveManager.instance;
    if (!save.canClaimReward()) { this.toast(t('reward.wait', { s: Math.ceil(save.rewardReadyInMs() / 1000) })); return; }
    Ads.rewarded(() => {
      save.grantRewardCoins(REWARD_COINS);
      this.toast(t('reward.got', { n: REWARD_COINS }));
      AudioEngine.chime();
      this.time.delayedCall(1400, () => this.scene.restart({ index: this.index })); // refresh chip + cooldown
    });
  }

  private toast(msg: string): void {
    const { cx, h, s } = viewport(this);
    const txt = this.add.text(cx, h * 0.88, msg, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(16 * s)}px`, color: '#6ef0a0', fontStyle: 'bold', backgroundColor: '#0b1020cc', padding: { x: 12, y: 6 } }).setOrigin(0.5).setDepth(60);
    this.tweens.add({ targets: txt, alpha: 0, delay: 1600, duration: 600, onComplete: () => txt.destroy() });
  }

  /** The refreshable right-side panel: name, fatigue, the five trainable stat rows. */
  private renderPanel(): void {
    const { w, h, s } = viewport(this);
    this.panelObjects.forEach((o) => o.destroy());
    this.panelObjects = [];
    const athlete = this.roster()[this.index]!;
    const specialtyStat = getDiscipline(athlete.specialty).primaryStat as StatKey;

    const panelX = w * 0.48;
    const panelW = w * 0.48;
    const panelCx = panelX + panelW / 2;
    const top = h * 0.16;
    const bottom = h - Math.max(72, h * 0.12) - 8 * s;
    this.panelObjects.push(drawPanel(this, panelCx, (top + bottom) / 2, panelW, bottom - top, { radius: 16, fillAlpha: 0.66 }));

    const left = panelX + 18 * s;
    const right = panelX + panelW - 18 * s;

    // Header: name + nickname.
    this.panelObjects.push(this.add.text(left, top + 16 * s, `${countryFlag(athlete.nationality)} ${athlete.name}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(17, Math.round(20 * s))}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(8));

    // Fatigue bar + label (now a live mechanic: tired athletes underperform until they rest).
    const fy = top + 42 * s;
    const fatigue = athlete.fatigue;
    const penaltyPct = Math.round((1 - fatiguePenalty(fatigue)) * 100);
    this.panelObjects.push(this.add.text(left, fy, `😮‍💨 ${t(`fat.${fatigueKey(fatigue)}`)}${penaltyPct > 0 ? `  −${penaltyPct}%` : ''}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(14 * s))}px`, color: penaltyPct > 6 ? '#ffb45d' : '#9fb0e0',
    }).setOrigin(0, 0.5).setDepth(8));
    this.bar(left + 150 * s, fy, right - (left + 150 * s), Math.max(8, 10 * s), fatigue / 100, PALETTE.bad, 0x2a3350);

    // Five stat rows.
    const rowsTop = fy + 26 * s;
    const rowH = (bottom - rowsTop - 10 * s) / STAT_KEYS.length;
    STAT_KEYS.forEach((stat, i) => {
      this.statRow(stat, stat === specialtyStat, athlete, left, right, rowsTop + i * rowH + rowH / 2, rowH, s);
    });
  }

  private statRow(stat: StatKey, isSpecialty: boolean, athlete: Athlete, left: number, right: number, y: number, rowH: number, s: number): void {
    const lvl = trainLevel(athlete, stat);
    const value = Math.min(STAT_CAP, athlete.stats[stat] + lvl * STAT_STEP);
    const cost = nextTrainCost(athlete, stat);
    const labelFs = Math.max(13, Math.round(14 * s));

    // Label (+ specialty star).
    this.panelObjects.push(this.add.text(left, y - rowH * 0.26, `${isSpecialty ? '⭐ ' : ''}${t(`stat.${stat}`)}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${labelFs}px`, color: isSpecialty ? '#ffd24a' : '#cfd8f5', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(8));
    this.panelObjects.push(this.add.text(left + 132 * s, y - rowH * 0.26, `${value}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${labelFs}px`, color: '#59d2ff', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setDepth(8));

    // Value bar + trained pips.
    const btnW = Math.max(78, 92 * s);
    const barRight = right - btnW - 12 * s;
    const barLeft = left;
    this.bar(barLeft, y + rowH * 0.14, barRight - barLeft, Math.max(9, 11 * s), value / 100, isSpecialty ? PALETTE.sun : PALETTE.laneAccent, 0x2a3350);
    // Pips show how many of the MAX_TRAIN_LEVEL upgrades are bought.
    const pipR = Math.max(2.5, 3 * s);
    const pipGap = Math.min(13 * s, (barRight - barLeft) / (MAX_TRAIN_LEVEL + 1));
    for (let p = 0; p < MAX_TRAIN_LEVEL; p++) {
      this.panelObjects.push(this.add.circle(barLeft + 4 + p * pipGap, y + rowH * 0.34, pipR, p < lvl ? PALETTE.good : PALETTE.ink, p < lvl ? 1 : 0.5).setDepth(9));
    }

    // Buy button: "+ NNN🪙", greyed when maxed or unaffordable.
    const maxed = cost == null;
    const afford = cost != null && SaveManager.instance.coins >= cost;
    const enabled = !maxed && afford;
    const bx = right - btnW / 2;
    const bh = Math.max(34, rowH * 0.66);
    const col = maxed ? PALETTE.trackLine : enabled ? PALETTE.good : PALETTE.ink;
    const btnG = this.add.graphics().setDepth(9);
    btnG.fillStyle(col, enabled ? 1 : 0.5).fillRoundedRect(bx - btnW / 2, y - bh / 2, btnW, bh, 10);
    const btnLabel = maxed ? t('train.max') : `+ ${cost}🪙`;
    const btnT = this.add.text(bx, y, btnLabel, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: maxed ? '#0b1020' : enabled ? '#0b1020' : '#8893b8', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10);
    this.panelObjects.push(btnG, btnT);
    if (enabled) {
      [btnG, btnT].forEach((o) => o.setInteractive(
        o === btnG ? new Phaser.Geom.Rectangle(bx - btnW / 2, y - bh / 2, btnW, bh) : undefined,
        o === btnG ? Phaser.Geom.Rectangle.Contains : undefined,
      ).on(Phaser.Input.Events.POINTER_DOWN, () => this.buy(stat)));
    }
  }

  private buy(stat: StatKey): void {
    const athlete = this.roster()[this.index]!;
    if (SaveManager.instance.trainStat(athlete.id, stat)) {
      AudioEngine.tap(0.6);
      AudioEngine.chime();
      this.coinChip?.setText(`🪙 ${SaveManager.instance.coins}`);
      this.renderPanel();
    } else {
      AudioEngine.tap(0.2);
    }
  }

  /** A simple filled bar with a track background. */
  private bar(x: number, y: number, width: number, height: number, fill: number, color: number, track: number): void {
    const g = this.add.graphics().setDepth(8);
    g.fillStyle(track, 0.8).fillRoundedRect(x, y - height / 2, width, height, height / 2);
    const fw = Math.max(0, Math.min(1, fill)) * width;
    if (fw > 1) g.fillStyle(color, 1).fillRoundedRect(x, y - height / 2, fw, height, height / 2);
    this.panelObjects.push(g);
  }

  private chip(rightX: number, y: number, s: number): Phaser.GameObjects.Text {
    const fs = Math.round(Math.max(17, 19 * s));
    const t = this.add.text(0, 0, `🪙 ${SaveManager.instance.coins}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' }).setShadow(0, 2, '#000000cc', 3);
    const padX = 14 * s;
    const ww = t.width + padX * 2;
    const hh = t.height + 12 * s;
    const bx = rightX - ww;
    this.add.graphics().setDepth(18)
      .fillStyle(PALETTE.ink, 0.78).fillRoundedRect(bx, y, ww, hh, hh / 2)
      .lineStyle(2, PALETTE.cadence, 0.6).strokeRoundedRect(bx, y, ww, hh, hh / 2);
    t.setPosition(bx + padX, y + hh / 2).setOrigin(0, 0.5).setDepth(19);
    return t;
  }

  private arrow(x: number, y: number, dir: number, s: number): void {
    const r = Math.max(28, 32 * s);
    const c = this.add.circle(x, y, r, PALETTE.ink, 0.45).setStrokeStyle(2, PALETTE.cadence, 0.7).setDepth(9);
    const t = this.add.text(x, y, dir < 0 ? '‹' : '›', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(40 * s)}px`, color: '#59d2ff', fontStyle: 'bold' }).setOrigin(0.5).setDepth(9);
    [c, t].forEach((o) => o.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => this.cycle(dir)));
  }

  private dots(cx: number, y: number, s: number): void {
    const n = this.roster().length;
    const gap = Math.min(16 * s, (this.scale.width * 0.4) / Math.max(n, 1));
    for (let i = 0; i < n; i++) {
      this.add.circle(cx + (i - (n - 1) / 2) * gap, y, i === this.index ? 5 * s : 3.5 * s, i === this.index ? PALETTE.laneAccent : PALETTE.trackLine, 1).setDepth(9);
    }
  }

  private cycle(dir: number): void {
    const n = this.roster().length;
    this.index = (this.index + dir + n) % n;
    AudioEngine.tap(0.3);
    this.scene.restart({ index: this.index });
  }

  update(_t: number, delta: number): void {
    this.rig?.update(delta / 1000);
  }
}
